import {Button, Modal} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {Text} from 'src/design/atoms/Text';
import {IconAlertCircle, IconCheck} from '@tabler/icons-react';
import {useState} from 'react';
import type {VendorResponse} from 'src/api/ProactApiClient';
import {useUpdateVendorActiveStatusMutation} from 'src/redux/queries/vendor';

export interface VendorActiveStatusTabProps {
  vendorId: number;
  isActive: boolean;
  onStatusChange: (vendor: VendorResponse) => void;
}

export const VendorActiveStatusTab: React.FC<VendorActiveStatusTabProps> = ({
  vendorId,
  isActive,
  onStatusChange,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [updateVendorActiveStatus, {isLoading: loading}] =
    useUpdateVendorActiveStatusMutation();

  const handleToggle = async () => {
    setError(null);
    try {
      const response = await updateVendorActiveStatus({
        id: vendorId,
        active: !isActive,
      }).unwrap();
      onStatusChange(response);
      setModalOpen(false);
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : 'Failed to update status.'
      );
    }
  };

  return (
    <Box mt="md">
      <Alert
        icon={<IconAlertCircle />}
        color={'red'}
        title={isActive ? 'Disable Vendor' : 'Enable Vendor'}
        mb="md">
        <Text size="sm" mb="md">
          {isActive
            ? 'Disabling this vendor will deactivate all associated signup URLs and prevent new personnel from being added. Existing users will remain but will be marked inactive.'
            : 'Enabling this vendor will reactivate all associated signup URLs and allow new personnel to be added. Inactive users are not reactivated.'}
        </Text>
        <Text size="sm" mb="md">
          An audit record will be created for this action.
        </Text>
        <Button
          size="sm"
          color={isActive ? 'red' : 'green'}
          leftSection={isActive ? <IconAlertCircle /> : <IconCheck />}
          onClick={() => setModalOpen(true)}
          loading={loading}>
          {isActive ? 'Disable Vendor' : 'Enable Vendor'}
        </Button>
      </Alert>
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={isActive ? 'Confirm Disable Vendor' : 'Confirm Enable Vendor'}
        centered>
        <Text mb="md">
          {isActive
            ? 'Are you sure you want to disable this vendor? This action will deactivate all signup URLs and prevent new personnel from being added. Existing users will remain but will be marked inactive. No users that are assigned to this vendor will be able to login.'
            : 'Are you sure you want to enable this vendor? This will reactivate all signup URLs and allow new personnel to be added.'}
        </Text>
        {error && (
          <Alert color="red" mb="md">
            {error}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button
            size="sm"
            color="black"
            variant="light"
            onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            color={isActive ? 'red' : 'green'}
            onClick={handleToggle}
            loading={loading}>
            {isActive
              ? 'I am sure, disable vendor'
              : 'I am sure, enable vendor'}
          </Button>
        </Group>
      </Modal>
    </Box>
  );
};
